// CLP editor — sectioned inputs (setup, programme & vehicles), outputs, versions.
import { useCallback, useEffect, useState, Fragment } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Save, Loader2, Plus, Trash2, Truck, Settings, BarChart3,
    History, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import ClpOutputs from "./ClpOutputs";
import { buildAxis, inWindow, monthLabel, parseYm, fmtNum } from "@/lib/clp";

const CAN_EDIT = new Set(["director", "administrator", "consultant"]);
const STATUS_TONE = { Draft: "neutral", "In review": "amber", Final: "green", Superseded: "neutral" };
const STATUSES = ["Draft", "In review", "Final", "Superseded"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

export default function ClpEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const backUrl = params.get("back");
    const { user } = useAuth();
    const { isInternalView } = useView();
    const canEdit = CAN_EDIT.has(user?.role) && isInternalView;

    const [rec, setRec] = useState(null);
    const [tab, setTab] = useState("setup");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [meta, setMeta] = useState({ default_stages: { Building: [], Infrastructure: [] } });

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/clp/${id}`);
            setRec(data);
        } catch (e) {
            toast.error("Failed to load CLP");
            navigate("/app/clp");
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { api.get("/clp/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

    const patch = (changes) => { setRec((r) => ({ ...r, ...changes })); setDirty(true); };

    const save = useCallback(async () => {
        if (!rec) return null;
        setSaving(true);
        try {
            const body = {
                project_id: rec.project_id || null, building_id: rec.building_id || null, structure: rec.structure || null,
                development_name: rec.development_name, landowner: rec.landowner,
                principal_contractor: rec.principal_contractor, site_address: rec.site_address,
                site_postcode: rec.site_postcode, consolidation_centre_postcode: rec.consolidation_centre_postcode,
                project_type: rec.project_type, construction_type: rec.construction_type,
                site_state: rec.site_state, working_days_per_month: Number(rec.working_days_per_month) || 0,
                gfa: rec.gfa, stages: rec.stages, status: rec.status,
            };
            const { data } = await api.patch(`/clp/${id}`, body);
            setRec(data); setDirty(false);
            return data;
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
            return null;
        } finally { setSaving(false); }
    }, [rec, id]);

    const goTab = async (t) => {
        if (t === "outputs" && dirty && canEdit) { await save(); }
        setTab(t);
    };

    if (!rec) return <div className="grid place-items-center py-32"><Loader2 className="w-7 h-7 animate-spin text-[#C75A1B]" /></div>;

    const axis = buildAxis(rec.stages);
    const tabs = [
        { id: "setup", label: "Setup", icon: Settings },
        { id: "programme", label: "Programme & Vehicles", icon: Truck },
        { id: "outputs", label: "Outputs & Charts", icon: BarChart3 },
        { id: "versions", label: "Versions", icon: History },
    ];

    return (
        <div className="p-6 max-w-[1280px] mx-auto" data-testid="clp-editor">
            <button onClick={() => navigate(backUrl || "/app/clp")} className="inline-flex items-center gap-1.5 text-sm text-[#6F6A64] hover:text-[#1F1F1F] mb-3">
                <ArrowLeft className="w-4 h-4" /> {backUrl ? "Back to project" : "All CLPs"}
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2.5">
                        <h1 className="text-2xl font-serif text-[#1F1F1F]">{rec.development_name || "Untitled CLP"}</h1>
                        <Pill tone={STATUS_TONE[rec.status]}>{rec.status}</Pill>
                    </div>
                    <p className="text-sm text-[#6F6A64] mt-1">{rec.reference} · v{rec.version} · {rec.project_type}</p>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <select data-testid="clp-status-select" value={rec.status} onChange={(e) => patch({ status: e.target.value })}
                            className="border rounded-lg px-2.5 py-2 text-sm" style={{ borderColor: "#E5E0DA" }}>
                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                    )}
                    {canEdit && (
                        <button onClick={save} disabled={saving || !dirty} data-testid="clp-save-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                            style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {dirty ? "Save changes" : "Saved"}
                        </button>
                    )}
                </div>
            </div>

            <div className="flex gap-1 mt-5 border-b" style={{ borderColor: "#E5E0DA" }}>
                {tabs.map((t) => (
                    <button key={t.id} onClick={() => goTab(t.id)} data-testid={`clp-tab-${t.id}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "" : "border-transparent text-[#6F6A64] hover:text-[#1F1F1F]"}`}
                        style={tab === t.id ? { borderColor: "var(--brand-accent,#C75A1B)", color: "var(--brand-accent,#C75A1B)" } : {}}>
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </div>

            <div className="mt-5">
                {tab === "setup" && <SetupTab rec={rec} patch={patch} canEdit={canEdit} meta={meta} />}
                {tab === "programme" && <ProgrammeTab rec={rec} setRec={(r) => { setRec(r); setDirty(true); }} canEdit={canEdit} axis={axis} />}
                {tab === "outputs" && <ClpOutputs clpId={id} version={`${rec.updated_at}-${dirty}`} />}
                {tab === "versions" && <VersionsTab rec={rec} reload={load} canEdit={canEdit} />}
            </div>
        </div>
    );
}

// ---------------- Setup ----------------
const F = ({ label, children }) => (
    <div><label className="text-xs font-semibold text-[#6F6A64]">{label}</label>{children}</div>
);
const inputCls = "w-full border rounded-lg px-3 py-2 text-sm mt-0.5";

function SetupTab({ rec, patch, canEdit, meta }) {
    const [projects, setProjects] = useState([]);
    const [buildings, setBuildings] = useState([]);
    useEffect(() => {
        if (!rec.client_id) return;
        api.get(`/projects?client_id=${rec.client_id}`).then(({ data }) => setProjects(data || [])).catch(() => {});
        api.get(`/buildings?client_id=${rec.client_id}`).then(({ data }) => setBuildings(data || [])).catch(() => {});
    }, [rec.client_id]);

    const txt = (key, label, ph = "") => (
        <F label={label}>
            <input className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit} placeholder={ph}
                data-testid={`clp-field-${key}`} value={rec[key] || ""} onChange={(e) => patch({ [key]: e.target.value })} />
        </F>
    );
    const sel = (key, label, opts) => (
        <F label={label}>
            <select className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                data-testid={`clp-field-${key}`} value={rec[key] || ""} onChange={(e) => patch({ [key]: e.target.value })}>
                <option value="">—</option>
                {opts.map((o) => <option key={o}>{o}</option>)}
            </select>
        </F>
    );
    const gfaTotals = (rec.gfa || []).reduce((a, r) => ({
        n: a.n + (Number(r.new_area) || 0), r: a.r + (Number(r.refurb_area) || 0),
    }), { n: 0, r: 0 });

    return (
        <div className="space-y-5">
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-link-fields">
                <h3 className="font-semibold mb-3">Linked project</h3>
                <div className="grid sm:grid-cols-3 gap-3">
                    <F label="Project">
                        <select className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                            data-testid="clp-assign-project" value={rec.project_id || ""}
                            onChange={(e) => patch({ project_id: e.target.value || null })}>
                            <option value="">— Not assigned —</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.reference} · {p.name}</option>)}
                        </select>
                    </F>
                    <F label="Structure / building">
                        <select className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                            data-testid="clp-assign-building" value={rec.building_id || ""}
                            onChange={(e) => patch({ building_id: e.target.value || null })}>
                            <option value="">— None —</option>
                            {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </F>
                    <F label="Location / structure note">
                        <input className={inputCls} style={{ borderColor: "#E5E0DA" }} disabled={!canEdit}
                            data-testid="clp-assign-structure" value={rec.structure || ""}
                            onChange={(e) => patch({ structure: e.target.value })} />
                    </F>
                </div>
            </div>

            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-setup-fields">
                <h3 className="font-semibold mb-3">User inputs</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {txt("development_name", "Development name")}
                    {txt("landowner", "Landowner")}
                    {txt("principal_contractor", "Principal contractor")}
                    {txt("site_address", "Site address")}
                    {txt("site_postcode", "Site postcode")}
                    {txt("consolidation_centre_postcode", "Consolidation centre postcode")}
                    {sel("project_type", "Type of project", ["Building", "Infrastructure"])}
                    {sel("construction_type", "Type of construction", ["Renovation of existing building", "New build"])}
                    {sel("site_state", "State of current site", ["Vacant", "Structures to be demolished", "Structures to be retained"])}
                    <F label="Working days per month">
                        <input type="number" min="1" className={inputCls} style={{ borderColor: (Number(rec.working_days_per_month) > 0) ? "#E5E0DA" : "#B33A2E" }}
                            disabled={!canEdit} data-testid="clp-field-working_days_per_month"
                            value={rec.working_days_per_month ?? ""} onChange={(e) => patch({ working_days_per_month: e.target.value })} />
                    </F>
                </div>
            </div>

            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-gfa-table">
                <h3 className="font-semibold mb-3">Gross floor area (sqm)</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-[#6F6A64] text-xs">
                            <th className="text-left py-1.5">Use class</th>
                            <th className="text-right">New / additional</th>
                            <th className="text-right">Refurbished</th>
                            <th className="text-right">Total</th>
                        </tr></thead>
                        <tbody>
                            {(rec.gfa || []).map((row, i) => (
                                <tr key={row.category} className="border-t" style={{ borderColor: "#EDE9E3" }}>
                                    <td className="py-1.5">{row.category}</td>
                                    {["new_area", "refurb_area"].map((k) => (
                                        <td key={k} className="text-right">
                                            <input type="number" min="0" disabled={!canEdit}
                                                data-testid={`clp-gfa-${i}-${k}`}
                                                className="w-24 border rounded px-2 py-1 text-right text-sm" style={{ borderColor: "#E5E0DA" }}
                                                value={row[k] || ""} onChange={(e) => {
                                                    const gfa = rec.gfa.map((g, gi) => gi === i ? { ...g, [k]: Number(e.target.value) || 0 } : g);
                                                    patch({ gfa });
                                                }} />
                                        </td>
                                    ))}
                                    <td className="text-right tabular-nums pr-1">{fmtNum((Number(row.new_area) || 0) + (Number(row.refurb_area) || 0))}</td>
                                </tr>
                            ))}
                            <tr className="border-t font-semibold" style={{ borderColor: "#D8D2C8" }}>
                                <td className="py-1.5">Total</td>
                                <td className="text-right pr-1 tabular-nums">{fmtNum(gfaTotals.n)}</td>
                                <td className="text-right pr-1 tabular-nums">{fmtNum(gfaTotals.r)}</td>
                                <td className="text-right pr-1 tabular-nums">{fmtNum(gfaTotals.n + gfaTotals.r)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ---------------- Programme & Vehicles ----------------
function ProgrammeTab({ rec, setRec, canEdit, axis }) {
    const stages = rec.stages || [];
    const setStages = (next) => setRec({ ...rec, stages: next });
    const updateStage = (sid, p) => setStages(stages.map((s) => s.id === sid ? { ...s, ...p } : s));
    const days = Number(rec.working_days_per_month) || 0;

    const addStage = () => setStages([...stages, {
        id: crypto.randomUUID(), name: "New stage", start_month: "", end_month: "",
        monthly_vehicles: {}, type_split: { le35: 0, mid: 0, ge75: 0 }, hourly: Array(24).fill(0),
    }]);
    const removeStage = (sid) => setStages(stages.filter((s) => s.id !== sid));

    const monthlyTotals = axis.map((k) => stages.reduce((a, s) => a + (Number(s.monthly_vehicles?.[k]) || 0), 0));

    return (
        <div className="space-y-5">
            {/* Stages */}
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-stages">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Construction stages</h3>
                    {canEdit && <button onClick={addStage} data-testid="clp-add-stage"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg"
                        style={{ background: "#FBEFE5", color: "var(--brand-accent,#C75A1B)" }}><Plus className="w-4 h-4" /> Add stage</button>}
                </div>
                <div className="space-y-2">
                    {stages.map((s) => {
                        const a = parseYm(s.start_month), b = parseYm(s.end_month);
                        const bad = a && b && (b[0] * 12 + b[1]) < (a[0] * 12 + a[1]);
                        return (
                            <div key={s.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`clp-stage-${s.id}`}>
                                <input className="col-span-5 border rounded-lg px-2.5 py-1.5 text-sm" style={{ borderColor: "#E5E0DA" }}
                                    disabled={!canEdit} value={s.name} data-testid={`clp-stage-name-${s.id}`}
                                    onChange={(e) => updateStage(s.id, { name: e.target.value })} />
                                <input type="month" className="col-span-3 border rounded-lg px-2 py-1.5 text-sm"
                                    style={{ borderColor: bad ? "#B33A2E" : "#E5E0DA" }} disabled={!canEdit}
                                    value={s.start_month || ""} data-testid={`clp-stage-start-${s.id}`}
                                    onChange={(e) => updateStage(s.id, { start_month: e.target.value })} />
                                <input type="month" className="col-span-3 border rounded-lg px-2 py-1.5 text-sm"
                                    style={{ borderColor: bad ? "#B33A2E" : "#E5E0DA" }} disabled={!canEdit}
                                    value={s.end_month || ""} data-testid={`clp-stage-end-${s.id}`}
                                    onChange={(e) => updateStage(s.id, { end_month: e.target.value })} />
                                {canEdit ? (
                                    <button onClick={() => removeStage(s.id)} data-testid={`clp-stage-remove-${s.id}`}
                                        className="col-span-1 grid place-items-center text-[#B33A2E] hover:bg-[#FBE8E5] rounded-lg py-1.5">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                ) : <span className="col-span-1" />}
                                {bad && <p className="col-span-12 text-xs text-[#B33A2E] flex items-center gap-1 -mt-1">
                                    <AlertCircle className="w-3 h-3" /> End month is before start month.</p>}
                            </div>
                        );
                    })}
                    {stages.length === 0 && <p className="text-sm text-[#6F6A64]">No stages yet — add one to begin.</p>}
                </div>
            </div>

            {/* Monthly vehicle grid */}
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-monthly-grid">
                <h3 className="font-semibold">Monthly vehicle movements</h3>
                <p className="text-xs text-[#6F6A64] mt-0.5 mb-3">Enter expected vehicle trips per stage, per month. Highlighted cells are within each stage's active window.</p>
                {axis.length === 0 ? <p className="text-sm text-[#6F6A64]">Set stage start/end dates to build the programme timeline.</p> : (
                    <div className="overflow-x-auto">
                        <table className="text-sm border-collapse">
                            <thead>
                                <tr>
                                    <th className="sticky left-0 bg-white text-left px-2 py-1.5 min-w-[170px] z-10">Stage</th>
                                    {axis.map((k) => <th key={k} className="px-1 py-1.5 text-[10px] text-[#6F6A64] font-medium whitespace-nowrap">{monthLabel(k)}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {stages.map((s) => (
                                    <tr key={s.id} className="border-t" style={{ borderColor: "#EDE9E3" }}>
                                        <td className="sticky left-0 bg-white px-2 py-1 text-[12px] truncate max-w-[170px] z-10">{s.name}</td>
                                        {axis.map((k) => {
                                            const active = inWindow(s, k);
                                            return (
                                                <td key={k} className="px-0.5 py-0.5">
                                                    <input type="number" min="0" disabled={!canEdit}
                                                        data-testid={`clp-cell-${s.id}-${k}`}
                                                        className="w-14 text-center rounded px-1 py-1 text-[12px] border"
                                                        style={{ borderColor: active ? "#E5C9B3" : "#EFEDE9", background: active ? "#FFF7F0" : "#FBFAF8" }}
                                                        value={s.monthly_vehicles?.[k] ?? ""}
                                                        onChange={(e) => {
                                                            const mv = { ...(s.monthly_vehicles || {}) };
                                                            const val = e.target.value;
                                                            if (val === "") delete mv[k]; else mv[k] = Number(val) || 0;
                                                            updateStage(s.id, { monthly_vehicles: mv });
                                                        }} />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                <tr className="border-t-2 font-semibold" style={{ borderColor: "#D8D2C8" }}>
                                    <td className="sticky left-0 bg-white px-2 py-1.5 text-[12px] z-10">Monthly total</td>
                                    {monthlyTotals.map((t, i) => <td key={i} className="text-center text-[12px] tabular-nums">{fmtNum(t)}</td>)}
                                </tr>
                                <tr className="text-[#6F6A64]">
                                    <td className="sticky left-0 bg-white px-2 py-1 text-[11px] z-10">Avg daily total</td>
                                    {monthlyTotals.map((t, i) => <td key={i} className="text-center text-[11px] tabular-nums">{days ? fmtNum(t / days) : "—"}</td>)}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Vehicle type split */}
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-type-split">
                <h3 className="font-semibold">Vehicle type split by stage</h3>
                <p className="text-xs text-[#6F6A64] mt-0.5 mb-3">Proportion of each stage's vehicles by weight class (e.g. 0.4 = 40%).</p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-[#6F6A64] text-xs">
                            <th className="text-left py-1.5">Stage</th>
                            <th className="text-right">≤ 3.5t</th><th className="text-right">3.5t – 7.5t</th>
                            <th className="text-right">≥ 7.5t</th><th className="text-right">Total</th>
                        </tr></thead>
                        <tbody>
                            {stages.map((s) => {
                                const ts = s.type_split || {};
                                const tot = (Number(ts.le35) || 0) + (Number(ts.mid) || 0) + (Number(ts.ge75) || 0);
                                return (
                                    <tr key={s.id} className="border-t" style={{ borderColor: "#EDE9E3" }}>
                                        <td className="py-1.5">{s.name}</td>
                                        {["le35", "mid", "ge75"].map((k) => (
                                            <td key={k} className="text-right">
                                                <input type="number" step="0.05" min="0" disabled={!canEdit}
                                                    data-testid={`clp-split-${s.id}-${k}`}
                                                    className="w-20 border rounded px-2 py-1 text-right text-sm" style={{ borderColor: "#E5E0DA" }}
                                                    value={ts[k] ?? ""} onChange={(e) => updateStage(s.id, { type_split: { ...ts, [k]: Number(e.target.value) || 0 } })} />
                                            </td>
                                        ))}
                                        <td className="text-right tabular-nums pr-1">{fmtNum(tot, 2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Hourly distribution */}
            <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }} data-testid="clp-hourly-grid">
                <h3 className="font-semibold">Hourly distribution by stage</h3>
                <p className="text-xs text-[#6F6A64] mt-0.5 mb-3">Proportion of a typical day's vehicles arriving in each hour (0–1).</p>
                <div className="overflow-x-auto">
                    <table className="text-sm border-collapse">
                        <thead><tr>
                            <th className="sticky left-0 bg-white text-left px-2 py-1.5 min-w-[160px] z-10">Stage</th>
                            {HOURS.map((h) => <th key={h} className="px-1 text-[10px] text-[#6F6A64] font-medium">{String(h).padStart(2, "0")}</th>)}
                            <th className="px-1 text-[10px] text-[#6F6A64] font-medium">Σ</th>
                        </tr></thead>
                        <tbody>
                            {stages.map((s) => {
                                const hourly = s.hourly && s.hourly.length === 24 ? s.hourly : Array(24).fill(0);
                                const sum = hourly.reduce((a, v) => a + (Number(v) || 0), 0);
                                return (
                                    <tr key={s.id} className="border-t" style={{ borderColor: "#EDE9E3" }}>
                                        <td className="sticky left-0 bg-white px-2 py-1 text-[12px] truncate max-w-[160px] z-10">{s.name}</td>
                                        {HOURS.map((h) => (
                                            <td key={h} className="px-0.5">
                                                <input type="number" step="0.05" min="0" disabled={!canEdit}
                                                    data-testid={`clp-hour-${s.id}-${h}`}
                                                    className="w-11 text-center rounded px-0.5 py-1 text-[11px] border" style={{ borderColor: "#EFEDE9" }}
                                                    value={hourly[h] || ""} onChange={(e) => {
                                                        const next = [...hourly]; next[h] = Number(e.target.value) || 0;
                                                        updateStage(s.id, { hourly: next });
                                                    }} />
                                            </td>
                                        ))}
                                        <td className="text-center text-[11px] tabular-nums font-semibold">{fmtNum(sum, 2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ---------------- Versions ----------------
function VersionsTab({ rec, reload, canEdit }) {
    const [label, setLabel] = useState("");
    const [saving, setSaving] = useState(false);
    const [cmp, setCmp] = useState(null);
    const versions = rec.versions || [];

    const snapshot = async () => {
        setSaving(true);
        try {
            await api.post(`/clp/${rec.id}/versions`, { label: label || null });
            toast.success("Version snapshot saved");
            setLabel(""); await reload();
        } catch (e) { toast.error("Snapshot failed"); }
        finally { setSaving(false); }
    };

    const compare = async (a, b) => {
        try {
            const { data } = await api.get(`/clp/${rec.id}/versions/compare?a=${a}&b=${b}`);
            setCmp(data);
        } catch (e) { toast.error("Compare failed"); }
    };

    return (
        <div className="space-y-4 max-w-3xl">
            {canEdit && (
                <div className="bg-white border rounded-2xl p-4 flex items-end gap-3" style={{ borderColor: "#E5E0DA" }}>
                    <F label="Snapshot label (optional)">
                        <input className={inputCls} style={{ borderColor: "#E5E0DA" }} data-testid="clp-version-label"
                            value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Issued to client v2" />
                    </F>
                    <button onClick={snapshot} disabled={saving} data-testid="clp-snapshot-button"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 whitespace-nowrap"
                        style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />} Snapshot current
                    </button>
                </div>
            )}

            <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#E5E0DA" }} data-testid="clp-version-list">
                <h3 className="font-semibold mb-2">Version history</h3>
                {versions.length === 0 ? <p className="text-sm text-[#6F6A64]">No snapshots yet. The working version is v{rec.version}.</p> : (
                    <ul className="divide-y" style={{ borderColor: "#EDE9E3" }}>
                        {[...versions].reverse().map((v) => (
                            <li key={v.id} className="py-2.5 flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-semibold">v{v.version} {v.label && <span className="font-normal text-[#6F6A64]">· {v.label}</span>}</div>
                                    <div className="text-xs text-[#9A958C]">{new Date(v.created_at).toLocaleString()} · {v.created_by_name || "—"}</div>
                                    <div className="text-xs text-[#6F6A64] mt-0.5">
                                        Peak {v.summary?.peak_month_label || "—"} · {fmtNum(v.summary?.peak_month_total)} trips/mo · {v.summary?.stage_count || 0} stages
                                    </div>
                                </div>
                                <button onClick={() => compare(v.version, rec.version)} data-testid={`clp-compare-${v.version}`}
                                    className="text-xs font-semibold px-2.5 py-1.5 rounded-lg" style={{ background: "#F0EDE8", color: "#6F6A64" }}>
                                    Compare to current
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {cmp && (
                <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#E5E0DA" }} data-testid="clp-compare-result">
                    <h3 className="font-semibold mb-3">Comparison</h3>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="font-semibold text-[#6F6A64]">Metric</div>
                        <div className="font-semibold">v{cmp.a.version} {cmp.a.label && `(${cmp.a.label})`}</div>
                        <div className="font-semibold">v{cmp.b.version} {cmp.b.label && `(${cmp.b.label})`}</div>
                        {[["Peak month", "peak_month_label"], ["Peak monthly trips", "peak_month_total"], ["Peak daily", "peak_daily"], ["Programme months", "programme_months"], ["Stages", "stage_count"], ["Total vehicles", "total_vehicles"]].map(([lbl, k]) => (
                            <Fragment key={lbl}>
                                <div className="text-[#6F6A64]">{lbl}</div>
                                <div>{typeof cmp.a.summary?.[k] === "number" ? fmtNum(cmp.a.summary[k]) : (cmp.a.summary?.[k] || "—")}</div>
                                <div>{typeof cmp.b.summary?.[k] === "number" ? fmtNum(cmp.b.summary[k]) : (cmp.b.summary?.[k] || "—")}</div>
                            </Fragment>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
